import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'Simplified VAT Invoice UK — Rules, Threshold & What to Include',
      'description': 'A simplified VAT invoice can be used for UK retail supplies up to £250. Learn exactly what fields are required, when you can use one, and when a full VAT invoice is needed instead.',
      'url': 'https://www.invilic.com/blog/simplified-vat-invoice',
      'datePublished': '2026-06-01',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What is a simplified VAT invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A simplified VAT invoice is a reduced-format VAT invoice permitted by HMRC for retail supplies where the VAT-inclusive total does not exceed £250. It requires fewer fields than a full VAT invoice — specifically, it does not need the customer\'s name and address, the net amount, or the VAT amount as a separate line. The total including VAT and the VAT rate are sufficient.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the threshold for a simplified VAT invoice in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The threshold is £250 VAT-inclusive (i.e. the total amount the customer pays, including VAT). If the VAT-inclusive total exceeds £250, you cannot issue a simplified invoice — you must issue a full VAT invoice. The £250 threshold has been in place for many years and applies to retail supplies only.' },
        },
        {
          '@type': 'Question',
          'name': 'Can a B2B customer reclaim VAT from a simplified invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'Yes, a VAT-registered business customer can reclaim input VAT using a simplified invoice, provided the invoice meets all the required fields. However, because the simplified invoice does not separately show the net amount and VAT amount, some customers prefer a full VAT invoice for their accounting records. Always ask your customer which format they prefer.' },
        },
        {
          '@type': 'Question',
          'name': 'Do I need to show the VAT amount separately on a simplified invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'No. A simplified VAT invoice only needs to show the total amount payable including VAT, and the VAT rate that applies. The separate VAT amount does not need to be shown. This is the key difference from a full VAT invoice, which must show the net amount, the VAT amount, and the gross total as separate lines.' },
        },
        {
          '@type': 'Question',
          'name': 'When can I use a simplified VAT invoice instead of a full one?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'You can use a simplified VAT invoice when: (1) you are VAT-registered, (2) the supply is a retail supply (not a service sold to a business that will reclaim VAT), and (3) the VAT-inclusive total does not exceed £250. Common examples include shops, cafes, taxis, parking charges, and small retail transactions.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const simplifiedRequired = [
  'The words "VAT Invoice"',
  'Your trading name, address, and VAT registration number',
  'The date of supply (tax point)',
  'A description of the goods or services supplied',
  'The VAT rate applicable to each item',
  'The total amount payable including VAT',
];

const fullRequired = [
  'The words "VAT Invoice"',
  'A unique sequential invoice number',
  'The invoice date and tax point (if different)',
  'Your full name, address, and VAT registration number',
  "Your customer's full name and address",
  'A description of the goods or services',
  'Quantity, unit price, and VAT rate per line item',
  'Total excluding VAT (net)',
  'Total VAT charged (separate line)',
  'Total amount payable including VAT',
];

export default function SimplifiedVatInvoicePage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Simplified VAT Invoice UK — Rules, Threshold & Required Fields | Invilic Blog"
        description="UK simplified VAT invoices can be used for retail supplies up to £250. Covers required fields, the £250 threshold, when to use it vs a full VAT invoice, and HMRC rules."
        canonical="https://www.invilic.com/blog/simplified-vat-invoice"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">UK VAT</span>
          </div>
          <span className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">UK VAT</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            Simplified VAT Invoice UK — Rules, Threshold &amp; What to Include
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">June 1, 2026 · 5 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            A simplified VAT invoice is a streamlined version of the standard UK VAT invoice, permitted by HMRC for retail supplies where the total amount — including VAT — does not exceed <strong className="font-semibold text-gray-900 dark:text-white">£250</strong>. It requires fewer fields than a full VAT invoice, making it practical for shops, cafes, and service providers handling high volumes of small transactions.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Is a Simplified VAT Invoice?</h2>
          <p className="mb-4">
            Under UK VAT law (as set out in HMRC Notice 700), there are three types of VAT invoice: full, simplified, and modified. A simplified VAT invoice is the most basic of the three. It omits several fields that a full invoice requires — most notably the customer's name and address, the net amount, and the VAT amount as a separate line.
          </p>
          <p className="mb-6">
            The simplification exists because many retail transactions happen face-to-face, quickly, and at low values where recording the customer's full details is impractical. Think of a receipt from a shop, a parking ticket, or a taxi fare — these are classic simplified VAT invoices.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">The £250 Threshold — What It Means</h2>
          <p className="mb-4">
            The £250 threshold is the VAT-inclusive total — the actual amount the customer pays, including VAT. If a customer buys £210 of goods plus £42 VAT (20%) = £252 total, that exceeds the threshold, and a full VAT invoice is required.
          </p>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl mb-6 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">Common mistake:</strong> Some businesses mistakenly apply the threshold to the net (ex-VAT) amount. The threshold applies to the gross VAT-inclusive total. Always check the customer's total payment, not the pre-tax amount.
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Required Fields — Simplified vs Full VAT Invoice</h2>
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div>
              <p className="font-bold text-gray-900 dark:text-white mb-3 text-sm">Simplified VAT Invoice (up to £250)</p>
              <div className="space-y-2">
                {simplifiedRequired.map(f => (
                  <div key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="font-bold text-gray-900 dark:text-white mb-3 text-sm">Full VAT Invoice (all B2B transactions)</p>
              <div className="space-y-2">
                {fullRequired.map(f => (
                  <div key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <BlogCTA
            text="Create a full HMRC-compliant UK VAT invoice with all mandatory fields in seconds. Free, no sign-up required."
            to="/vat-invoice-generator"
            btnText="Create VAT Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Who Can Use a Simplified VAT Invoice?</h2>
          <p className="mb-4">Simplified VAT invoices are appropriate for:</p>
          <ul className="space-y-2 mb-6 text-sm">
            {[
              'Retail shops issuing receipts for purchases under £250',
              'Cafes, restaurants, and food outlets',
              'Parking operators and toll booths',
              'Taxi and private hire operators',
              'Vending machines and automated payment systems',
              'Fast-food and takeaway businesses',
            ].map(item => (
              <li key={item} className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0 mt-2" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="mb-6">
            Professional service businesses — freelancers, consultants, agencies, contractors — almost never use simplified invoices. Their invoices go to VAT-registered business clients who need full VAT invoices to reclaim input tax.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Can Customers Reclaim VAT from a Simplified Invoice?</h2>
          <p className="mb-4">
            Yes. A VAT-registered business can reclaim input VAT using a simplified invoice, provided the invoice contains all the required fields. HMRC guidance confirms that simplified invoices are valid for input tax recovery.
          </p>
          <p className="mb-6">
            However, because the simplified invoice does not show the net amount or VAT amount as separate figures, many business customers prefer a full VAT invoice for their accounting records. It is always good practice to ask your customer which format they need before issuing — especially for amounts close to the £250 threshold.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What Is a Simplified VAT Receipt?</h2>
          <p className="mb-4">
            The terms "simplified VAT invoice" and "simplified VAT receipt" are used interchangeably in practice. Both refer to the same HMRC-permitted document. The word "receipt" is commonly used in retail (a shop receipt) while "invoice" is used in professional services.
          </p>
          <p className="mb-6">
            Regardless of which word appears on the document, what matters for VAT purposes is whether it contains the six required fields listed above. If it does, it is a valid simplified VAT document for input tax recovery.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Need a full UK VAT invoice instead? Create one free in Invilic — VAT auto-calculated, PDF download in seconds."
            to="/vat-invoice-generator"
            btnText="Create Full VAT Invoice"
          />

        </article>
        <RelatedArticles current="/blog/simplified-vat-invoice" />
      </main>
    </div>
  );
}
